/**
 * The document viewer for a chain of title, as one hook two controllers share.
 *
 * `useRegisterController` and `useWorkController` both render the provenance
 * library and both need "click a document, see it"; neither should own the
 * storage-signing that makes it possible. This hook sits between
 * `provenance.model.ts`, which knows which documents a click means, and
 * `spotlight/useSpotlight.ts`, which knows how to show them.
 *
 * Nothing is signed until something is opened — see the note on the hook.
 */

import { useCallback } from "react";

import { signProvenanceDocuments } from "../lib/provenanceDocuments.ts";
import { useSpotlight } from "../spotlight/useSpotlight.ts";
import type { SpotlightSource } from "../spotlight/spotlight.model.ts";
import { documentsToOpen } from "./provenance.model.ts";
import type { DocumentRow, ProvenanceItem } from "./provenance.model.ts";
import type { SurfaceConfig } from "./provenance.surfaces.ts";

/**
 * Opening a document attached to a chain of title.
 *
 * One instance per screen that renders the provenance library, so both
 * controllers get the behaviour without either of them holding storage state.
 *
 * Signing happens HERE and only when something is opened. Expanding a link with
 * twelve invoices on it costs no round trips until one of them is wanted — the
 * same argument the work record already makes for fetching confirmations
 * lazily — and a URL signed at the moment of use is seconds old rather than
 * having spent its ten minutes sitting in an open panel.
 */
export function useProvenanceSpotlight(items: ProvenanceItem[], surface: SurfaceConfig) {
  const spotlight = useSpotlight();
  const { open } = spotlight;

  const openDocument = useCallback(
    (entryId: string, documentId: string) => {
      const found = documentsToOpen(items, surface, entryId, documentId);
      // Null for a click on a row that is no longer in the chain — a stale
      // click after a re-read. Nothing to show, and nothing to report.
      if (!found) return;

      void open(() => resolve(found.documents), found.index);
    },
    [items, surface, open],
  );

  return { view: spotlight.view, openDocument, closeDocument: spotlight.close };
}

/**
 * The whole entry's documents, signed or blobbed in one go.
 *
 * The set rather than the one clicked, so paging inside the viewer works — and
 * it costs nothing, because signing a set is one round trip whichever way.
 */
async function resolve(
  documents: DocumentRow[],
): Promise<{ sources: SpotlightSource[]; release?: () => void }> {
  const paths = documents.flatMap((document) =>
    document.source.kind === "stored" ? [document.source.objectPath] : [],
  );
  const signed = await signProvenanceDocuments(paths);

  const blobs: string[] = [];
  const sources = documents.map((document) => {
    const url =
      document.source.kind === "stored"
        ? // Absent from the map means storage refused this one. Null carries
          // that through to a sentence rather than a broken frame.
          (signed.get(document.source.objectPath) ?? null)
        : trackBlob(URL.createObjectURL(document.source.file), blobs);

    return {
      id: document.id,
      caption: document.name,
      contentType: document.source.contentType,
      url,
    };
  });

  // Spread rather than `release: undefined`, which `exactOptionalPropertyTypes`
  // treats as a different thing from an absent key.
  return blobs.length === 0
    ? { sources }
    : // Only for a draft's local files. Nothing is revoked for a signed URL,
      // which is a string and holds no handle on anything.
      { sources, release: () => blobs.forEach((url) => URL.revokeObjectURL(url)) };
}

function trackBlob(url: string, into: string[]): string {
  into.push(url);
  return url;
}
